import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/v2/ui';
import { useAIFieldGeneration } from './useAIFieldGeneration';
import { AIFieldMenu, type AIFieldRunArgs } from './AIFieldMenu';
import { AIFieldPreview } from './AIFieldPreview';

export interface AIFieldButtonProps {
  productId: string;
  fieldType: string;
  channelId?: string;
}

/** ✨ + ▾ entry point: implicit-template generation for one product field. */
export function AIFieldButton({ productId, fieldType, channelId }: AIFieldButtonProps) {
  const { t } = useTranslation();
  const { generate, reset, loading, result, error } = useAIFieldGeneration();
  const [open, setOpen] = useState(false);

  const run = (args: AIFieldRunArgs) => {
    setOpen(true);
    void generate({ productId, fieldType, channelId, ...args });
  };

  return (
    <>
      <div className="inline-flex items-center">
        <Button
          variant="outline"
          size="sm"
          className="rounded-r-none"
          disabled={loading}
          onClick={() => run({})}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4 mr-2" />
          )}
          {t('aiField.generate')}
        </Button>
        <AIFieldMenu
          productId={productId}
          fieldType={fieldType}
          disabled={loading}
          onRun={run}
        />
      </div>

      <AIFieldPreview
        open={open}
        loading={loading}
        result={result}
        error={error}
        productId={productId}
        fieldType={fieldType}
        channelId={channelId}
        onClose={() => {
          setOpen(false);
          reset();
        }}
      />
    </>
  );
}
